import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, ErrorNote } from "@/components/ui";
import { DateField, Labeled, Select, TextField } from "@/components/Form";

export type FinanceLineKind = "payment" | "cost";

/** Payload shape for POST /bookings/{ref}/{payments,costs}. */
export interface FinanceLinePayload {
  amount_minor: number;
  currency: string | null;
  method?: string | null;
  reference?: string | null;
  paid_at?: string | null;
  cost_type?: string;
  supplier_name?: string | null;
  incurred_at?: string | null;
  /** The line an extra cost belongs to (owner rule 19); null = automatic. */
  booking_line_id?: string | null;
}

/** A line of the booking a cost can be attached to, in page order. The picker's own
 * first entry is «Автоматически» (the first line with a car / driver — the server picks). */
export interface CostLineOption {
  id: string;
  label: string;
}

const CURRENCIES = ["AED", "USD", "EUR", "RUB", "SAR"];

/** "12.50" -> 1250 minor units; null when not a positive number. */
function toMinor(major: string): number | null {
  const n = Number(major.trim());
  if (!major.trim() || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

export function FinanceLineForm({
  kind,
  defaultCurrency,
  lines = [],
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  kind: FinanceLineKind;
  defaultCurrency: string;
  /** For a cost: the booking's lines to choose from; «Автоматически» is preselected. */
  lines?: CostLineOption[];
  busy: boolean;
  error: string | null;
  onSubmit: (payload: FinanceLinePayload) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    amount: "",
    currency: defaultCurrency,
    method: "",
    reference: "",
    paid_at: "",
    cost_type: "",
    supplier_name: "",
    incurred_at: "",
    booking_line_id: "", // "" = automatic: the server picks the first line with a car / driver
  });
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });

  const amountMinor = toMinor(f.amount);
  const valid =
    amountMinor !== null && (kind !== "cost" || f.cost_type.trim() !== "");

  const submit = () => {
    if (amountMinor === null) return;
    const base: FinanceLinePayload = {
      amount_minor: amountMinor,
      currency: f.currency || null,
    };
    if (kind === "payment") {
      base.method = f.method || null;
      base.reference = f.reference || null;
      base.paid_at = f.paid_at || null;
    } else {
      base.cost_type = f.cost_type.trim();
      base.supplier_name = f.supplier_name || null;
      base.incurred_at = f.incurred_at || null;
      base.booking_line_id = f.booking_line_id || null;
    }
    onSubmit(base);
  };

  return (
    <Card>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Labeled label={t("fields.amount")}>
          <TextField
            type="number"
            min="0"
            step="0.01"
            value={f.amount}
            onChange={(e) => set("amount", e.target.value)}
            placeholder="0.00"
          />
        </Labeled>
        <Labeled label={t("fields.currency")}>
          <Select value={f.currency} onChange={(e) => set("currency", e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </Labeled>

        {kind === "payment" && (
          <>
            <Labeled label={t("fields.method")}>
              <TextField value={f.method} onChange={(e) => set("method", e.target.value)} placeholder="card, cash, transfer" />
            </Labeled>
            <Labeled label={t("fields.reference")}>
              <TextField value={f.reference} onChange={(e) => set("reference", e.target.value)} />
            </Labeled>
            <Labeled label={t("fields.paidAt")}>
              <DateField value={f.paid_at} onChange={(v) => set("paid_at", v)} />
            </Labeled>
          </>
        )}

        {kind === "cost" && (
          <>
            <Labeled label={t("fields.costType")}>
              <TextField value={f.cost_type} onChange={(e) => set("cost_type", e.target.value)} placeholder="driver, hotel, ticket" />
            </Labeled>
            <Labeled label={t("fields.supplier")}>
              <TextField value={f.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} />
            </Labeled>
            <Labeled label={t("fields.incurredAt")}>
              <DateField value={f.incurred_at} onChange={(v) => set("incurred_at", v)} />
            </Labeled>
            {lines.length > 0 && (
              <div className="sm:col-span-2 lg:col-span-3">
                <Labeled label={t("bookings.costLine")}>
                  <Select
                    value={f.booking_line_id}
                    onChange={(e) => set("booking_line_id", e.target.value)}
                  >
                    <option value="">{t("bookings.costLineAuto")}</option>
                    {lines.map((l) => (
                      <option key={l.id} value={l.id}>{l.label}</option>
                    ))}
                  </Select>
                </Labeled>
                <span className="mt-1 block text-xs text-sand-muted">
                  {t("bookings.costLineHint")}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button loading={busy} disabled={!valid} onClick={submit}>
          {t("common.save")}
        </Button>
        <button className="text-sm text-sand-muted hover:text-sand" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </Card>
  );
}
